/**
 * Typed API client for the Building Domain's unified activity log (docs/spec/concepts.md
 * #tapahtumaloki-activity-log-contract §2.2, read path B — story #863). `GET /activity` is the
 * URL-scoped collection (bug #1337), same vocabulary as Havainnot `GET /findings`. The
 * per-building `GET /buildings/{id}/activity` remains for a single known id. activity-feed.ts
 * composes only presentation (Finnish `summary` + route `link`) from the returned rows.
 */
import { request } from "@/lib/api/building";
import type { InventorySearch } from "@/lib/inventory-scope-codec";

// Every kind, English identifiers per §2.2. `agent_schedule_changed` (story #1633, task #1634)
// narrates an operator's Play/Pause action on a scheduled agent — its `detail` carries
// `{agent_id, agent_display_name, enabled}`. Building persists the last two for writes an agent
// makes through its APIs (ADR-027): `learning_state_changed` carries
// `{subject, key, name_fi, from_state, to_state, value?}` and `recipe_applied` carries
// `{recipe_id, outcome, source_agent_id, parameters, mechanisms}`.
export type ActivityKind =
  | "building_created"
  | "equipment_added"
  | "capability_wired"
  | "syota_submitted"
  | "kb_tick_finished"
  | "finding_event"
  | "agent_schedule_changed"
  | "learning_state_changed"
  | "recipe_applied";

export type ActivityCategory =
  "Analytiikka" | "Optimointi" | "Strategia" | "Konfiguraatio";

// Strategia carries no event kind (§2.2's kind→Toiminta table) — it is a valid, always-offered
// Toiminta filter value that resolves to zero rows until strategy-proposal kinds enter the feed.
// Selecting only it drives a dedicated "not in the feed yet" empty state (ActivityFeed) rather than
// the generic no-match state. Optimointi has `recipe_applied`.
export const CATEGORIES_WITHOUT_KINDS: ActivityCategory[] = ["Strategia"];

export interface ActivityEntityRef {
  type: string;
  id: string;
  // Operator-ready entity name (equipment tag/name, zone name, canonical signal name),
  // resolved server-side by the Building activity GET — never a frontend join. `null` for
  // building-level rows and for an entity that no longer resolves (renamed/deleted).
  label: string | null;
}

// The row's actor, already resolved to a proper-noun label by the backend (§2.2). `person` is a
// named user identity; `agent` is the named Kairos agent that produced the event (Datavahtimestari
// / Toimintatarkastaja); `system` has no single named actor (e.g. `kb_tick_finished`) and always
// carries `label: null`. Generic Finnish fallbacks for a `null` label are UI copy — see
// `actorDisplay` in activity-feed-filters.ts — never derived here.
export interface ActivityActor {
  kind: "person" | "agent" | "system";
  label: string | null;
}

// The backend's structured row basis. `summary`/`link` are composed frontend-side (activity-feed.ts)
// from `kind`/`where`/`detail` — the backend stays English-under-the-hood and free of frontend
// route knowledge.
export interface ActivityFeedRow {
  building_id: string;
  building_name: string;
  when: string; // ISO datetime
  category: ActivityCategory[];
  kind: ActivityKind;
  where: ActivityEntityRef;
  who: ActivityActor;
  detail: Record<string, unknown>;
}

export interface ListActivityOptions {
  categories?: ActivityCategory[];
  kinds?: ActivityKind[];
  limit?: number;
  offset?: number;
}

export const listActivity = (
  buildingId: string,
  opts: ListActivityOptions = {},
): Promise<ActivityFeedRow[]> => {
  const q = new URLSearchParams();
  for (const category of opts.categories ?? []) q.append("category", category);
  for (const kind of opts.kinds ?? []) q.append("kind", kind);
  if (opts.limit !== undefined) q.set("limit", String(opts.limit));
  if (opts.offset !== undefined) q.set("offset", String(opts.offset));
  const qs = q.toString();
  return request<ActivityFeedRow[]>(
    `/buildings/${buildingId}/activity${qs ? `?${qs}` : ""}`,
  );
};

export const listActivityForScope = (
  scope: InventorySearch,
  opts: ListActivityOptions = {},
): Promise<ActivityFeedRow[]> => {
  // Same query encoding as listFindingsForScope (#1251) — never a building_ids list.
  const q = new URLSearchParams();
  if (scope.building) q.set("building", scope.building);
  if (scope.group) q.set("group", scope.group);
  if (scope.city) q.set("city", scope.city);
  if (scope.type) q.set("type", scope.type);
  if (scope.status) q.set("building_status", scope.status);
  for (const category of opts.categories ?? []) q.append("category", category);
  for (const kind of opts.kinds ?? []) q.append("kind", kind);
  if (opts.limit !== undefined) q.set("limit", String(opts.limit));
  if (opts.offset !== undefined) q.set("offset", String(opts.offset));
  const qs = q.toString();
  return request<ActivityFeedRow[]>(`/activity${qs ? `?${qs}` : ""}`);
};
